/**
 * My Progress — the learner's own dashboard: mastery, a subject-filterable
 * objective table, a dated dialogue history with an improvement chart,
 * misconceptions to fix, recommendations, and saved quizzes.
 *
 * Mastery is the share of assessed objectives currently mastered, not a mark,
 * so it is never mapped onto an achievement level. The quiz score sits beside
 * it and is never added to it: quizzes are retakeable self-checks.
 */
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { DialogDetail } from "../../components/DialogDetail";
import { SavedQuizReport } from "../../components/SavedQuizReport";
import { ProgressChart } from "../../components/ProgressChart";
import { MathText } from "../../components/Math";
import type { MyProgress } from "../../types";

const STATUS_LABEL: Record<string, string> = {
  mastered: "Mastered",
  in_progress: "In progress",
  needs_review: "Needs review",
  not_started: "Not started",
};

export function ProgressPage() {
  const { user } = useAuth();
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [openQuiz, setOpenQuiz] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string>("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["my-progress", user?.id],
    queryFn: () => apiGet<MyProgress>("/api/learners/me/progress"),
  });

  const availableSubjects = useMemo(() => {
    if (!data?.objectives) return [];
    const set = new Set<string>();
    data.objectives.forEach((o) => {
      if (o.subject) set.add(o.subject);
    });
    return Array.from(set);
  }, [data]);

  const filteredObjectives = useMemo(() => {
    if (!data?.objectives) return [];
    if (selectedSubject === "all") return data.objectives;
    return data.objectives.filter(
      (o) => (o.subject || "").toLowerCase() === selectedSubject.toLowerCase()
    );
  }, [data, selectedSubject]);

  if (isLoading) return <p className="text-muted">Loading your progress…</p>;
  if (error || !data) return <p className="error-message">Couldn't load your progress.</p>;

  const chrono = [...data.dialogs].reverse();
  const quizzes = data.quizzes ?? [];

  if (
    data.stats.total_dialogs === 0 &&
    (!data.objectives || data.objectives.length === 0) &&
    quizzes.length === 0
  ) {
    return (
      <div className="progress-page">
        <h1>My Progress</h1>
        <div className="card text-center" style={{ padding: "40px 24px" }}>
          <span style={{ fontSize: "2.4rem", display: "block", marginBottom: 12 }}>📚</span>
          <h2>No dialogues completed yet</h2>
          <p className="text-muted" style={{ maxWidth: 480, margin: "0 auto 20px" }}>
            Complete your first guided Socratic dialogue to establish your mastery baseline and
            receive personalized diagnostic feedback.
          </p>
          <Link to="/learn" className="btn btn-primary">
            Explore Your Subjects →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-page">
      {/* Header & mastery */}
      <div className="progress-hero-caps card">
        <div className="progress-hero-main">
          <h1>My Progress</h1>
          <p className="hero-subtitle">
            Your understanding across every objective you've been assessed on.
          </p>
          <p className="progress-measure-note">
            Mastery is the percentage of assessed objectives currently mastered. Objective scores
            reflect your latest attempt; a previous lesson pass is kept.
          </p>
          {data.quiz_score != null && (
            <p className="progress-measure-note">
              Quiz score is the average of your latest quiz on each lesson. It is shown beside
              mastery and does not change it.
            </p>
          )}
        </div>

        <div className="progress-mastery-display">
          <div className="mastery-circle">
            <span className="mastery-percentage">{data.mastery}%</span>
            <span className="mastery-sub">overall mastery</span>
          </div>
          {data.quiz_score != null && (
            <div className="mastery-circle quiz-circle">
              <span className="mastery-percentage">{data.quiz_score}%</span>
              <span className="mastery-sub">quiz score</span>
            </div>
          )}
        </div>
      </div>

      {/* High-Level Stat Cards */}
      <div className="roadmap-metrics-grid progress-stats-grid">
        <div className="stat-card">
          <span className="stat-value">{data.stats.total_dialogs}</span>
          <span className="stat-label">Dialogues Completed</span>
        </div>
        <div className="stat-card">
          <span className="stat-value text-success">{data.stats.mastered}</span>
          <span className="stat-label">Objectives Mastered</span>
        </div>
        <div className="stat-card">
          <span className="stat-value text-warning">{data.stats.in_progress}</span>
          <span className="stat-label">In Progress</span>
        </div>
        <div className="stat-card">
          <span className="stat-value text-danger">{data.stats.needs_review}</span>
          <span className="stat-label">Needs Review</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{data.stats.total_quizzes ?? 0}</span>
          <span className="stat-label">Quizzes Taken</span>
        </div>
      </div>

      {/* Historical Mastery Trend */}
      {chrono.length >= 2 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3>Mastery Improvement Over Time</h3>
          <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: 16 }}>
            Your most recent {chrono.length} completed dialogues, oldest to newest. The dotted line
            is a 70% score reference.
          </p>
          <ProgressChart dialogs={chrono} />
        </div>
      )}

      {/* Recommendations & Action Plan */}
      {data.recommendations.length > 0 && (
        <div className="card report-section-block" style={{ marginBottom: 24 }}>
          <h3>Recommended Action Plan</h3>
          <div className="recommendations-grid">
            {data.recommendations.map((r, i) => (
              <div key={i} className="recommendation-item">
                <span className="recommendation-area">{r.area}</span>
                <p className="recommendation-suggestion">{r.suggestion}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Objective Mastery Matrix with Subject Filter */}
      <div className="card" style={{ marginBottom: 24, padding: "24px" }}>
        <div className="objectives-header-row">
          <div>
            <h3>Objective Mastery</h3>
            <p className="text-muted" style={{ fontSize: "0.85rem", margin: "2px 0 0" }}>
              Your latest score on each objective you've been assessed on.
            </p>
          </div>

          {availableSubjects.length > 1 && (
            <div className="segmented-control" role="tablist">
              <button
                type="button"
                className={`segmented-tab ${selectedSubject === "all" ? "active" : ""}`}
                onClick={() => setSelectedSubject("all")}
              >
                All Subjects
              </button>
              {availableSubjects.map((sub) => (
                <button
                  key={sub}
                  type="button"
                  className={`segmented-tab ${selectedSubject === sub ? "active" : ""}`}
                  onClick={() => setSelectedSubject(sub)}
                >
                  {sub}
                </button>
              ))}
            </div>
          )}
        </div>

        {filteredObjectives.length === 0 ? (
          <p className="text-muted" style={{ marginTop: 16 }}>No objectives assessed for this subject yet.</p>
        ) : (
          <div className="report-scroll" style={{ marginTop: 16 }}>
            <table className="rubric">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Curriculum Objective</th>
                  <th style={{ width: 100, textAlign: "right" }}>Score</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th style={{ width: 90, textAlign: "center" }}>Attempts</th>
                  <th style={{ width: 100, textAlign: "center" }}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {filteredObjectives.map((o, i) => {
                  const isGood = o.score >= 70;
                  const isMid = o.score >= 50 && o.score < 70;
                  return (
                    <tr key={i}>
                      <td style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                        {o.subject || "General"}
                      </td>
                      <td>
                        <strong>{o.description}</strong>
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: isGood ? "var(--success)" : isMid ? "var(--warning)" : "var(--mark)",
                        }}
                      >
                        {o.score}%
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            isGood ? "badge-success" : isMid ? "badge-warning" : "badge-danger"
                          }`}
                        >
                          {STATUS_LABEL[o.status] ?? o.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>{o.attempts}</td>
                      <td style={{ textAlign: "center", fontSize: "1.1rem" }}>
                        {o.trend === "up" || o.trend === "improving"
                          ? "📈"
                          : o.trend === "down" || o.trend === "declining"
                          ? "📉"
                          : "➡️"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dated Dialogue History with Expandable Memorandum */}
      {data.dialogs.length > 0 && (
        <div className="card" style={{ padding: "24px" }}>
          <h3>Your Recent Dialogues</h3>
          <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: 16 }}>
            Open a dialogue to review your answers, the feedback, and any misconceptions found.
          </p>

          <div className="dialog-history">
            {data.dialogs.map((d, i) => {
              const open = openSession === d.session_id;
              return (
                <div key={i} className="dialog-history-item">
                  <button
                    type="button"
                    className="dialog-history-header dialog-history-clickable"
                    onClick={() => setOpenSession(open ? null : d.session_id)}
                    aria-expanded={open}
                    aria-controls={`dialog-details-${d.session_id}`}
                  >
                    <span className="dialog-toggle">{open ? "▾" : "▸"}</span>
                    <span
                      className={`dialog-score ${
                        d.score >= 70 ? "text-success" : d.score >= 50 ? "text-warning" : "text-danger"
                      }`}
                    >
                      {d.score}%
                    </span>
                    {d.subject && <span className="browse-grade-tag">{d.subject}</span>}
                    <span className="dialog-lesson">{d.lesson_title || "Lesson Assessment"}</span>
                    <span className="text-muted">
                      {new Date(d.completed_at).toLocaleDateString("en-ZA", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    {d.duration_minutes != null && (
                      <span className="text-muted" title="Time taken">
                        ⏱ {d.duration_minutes} min
                      </span>
                    )}
                    {d.ready_to_advance && <span className="badge badge-success">Passed</span>}
                  </button>

                  {!open && d.misconceptions && d.misconceptions.length > 0 && (
                    <div className="dialog-misconceptions">
                      <span className="text-muted">Focus areas: </span>
                      {d.misconceptions.map((m, j) => (
                        <span key={j} className="badge badge-warning">
                          {m}
                        </span>
                      ))}
                    </div>
                  )}

                  {!open && d.calculation_errors && d.calculation_errors.length > 0 && (
                    <div className="dialog-misconceptions">
                      <span className="text-muted">Arithmetic check: </span>
                      {d.calculation_errors.map((c, j) => (
                        <span key={j} className="badge badge-primary">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}

                  <div id={`dialog-details-${d.session_id}`}>
                    {open && <DialogDetail url={`/api/learners/me/dialogs/${d.session_id}`} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Saved quizzes, each report reopened in place */}
      {quizzes.length > 0 && (
        <div className="card" style={{ padding: "24px", marginTop: 24 }}>
          <h3>Your Recent Quizzes</h3>
          <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: 16 }}>
            Open a quiz to see every question, your answer and the correct answer.
          </p>
          <div className="dialog-history">
            {quizzes.map((q) => {
              const open = openQuiz === q.id;
              return (
                <div key={q.id} className="dialog-history-item">
                  <button
                    type="button"
                    className="dialog-history-header dialog-history-clickable"
                    onClick={() => setOpenQuiz(open ? null : q.id)}
                    aria-expanded={open}
                    aria-controls={`quiz-details-${q.id}`}
                  >
                    <span className="dialog-toggle">{open ? "▾" : "▸"}</span>
                    <span
                      className={`dialog-score ${
                        q.score >= 70 ? "text-success" : q.score >= 50 ? "text-warning" : "text-danger"
                      }`}
                    >
                      {q.score}%
                    </span>
                    <span className="text-muted">
                      {q.correct}/{q.total}
                    </span>
                    {q.subject && <span className="browse-grade-tag">{q.subject}</span>}
                    <span className="dialog-lesson">{q.lesson_title || "Lesson quiz"}</span>
                    <span className="text-muted">
                      {new Date(q.completed_at).toLocaleDateString("en-ZA", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </button>
                  <div id={`quiz-details-${q.id}`}>
                    {open && <SavedQuizReport resultId={q.id} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
